/**
 * # S2p `GeometrySnapshot` — the pre-paint geometry artifact
 *
 * Engine-spec §2 stage row **S2p pre-paint**: `GEOMETRY dirt + plan →
 * GeometrySnapshot (offsets, sizes, scroll window — apart from content)`,
 * Chrome's property-trees analog. This is the render track's first slice
 * (engine-spec §8): a pure, DOM-free carve-out of the geometry the paint /
 * commit stages need, separated from cell content and render bindings.
 *
 * ## What this is
 *
 * A **flowing message** under the two-kind immutability law (engine-spec §3):
 * deep-frozen in dev (via `ArtifactFreeze.deep`), JSON-round-trippable
 * byte-for-byte (see `geometry-snapshot-serde.ts`), and built from plain
 * frozen objects + ordered arrays only — no `Map`/`Set`/class instances live
 * inside the message, so serialization is deterministic and the artifact is a
 * value, not a station. The build never reads a station ambiently: the frame
 * revision (`{ tau, epoch }`) is **caller-injected** — `tau` is the seal mark
 * the reader pinned, `epoch` is the geometry activation key — never sampled from a
 * wall-clock or a global.
 *
 * ## The scroll window is a standing-query window, not live scroll
 *
 * `window` is the **standing-query window geometry** (engine-spec §5/§9): the
 * bind variables (which columns, how wide the viewport) of the one standing
 * query that is the visible scene. It is NOT `scrollTop`/`scrollLeft`.
 * Compositor scroll (stage **SC**) moves the already-active output and may at
 * most enqueue COVERAGE work — it does **not** re-enter the pipeline, so it
 * never rebuilds this snapshot. `initialScrollTopPx` lives in `vertical`
 * (VerticalBands), not here, because it is a layout-computed seed (the first
 * body row's authored initial offset), not a live scroll position.
 *
 * ## What it EXCLUDES (and where that state lives)
 *
 * - **Cell values / content, render bindings, plugin ids, `RenderPlan`
 *   state** — those belong to the content path (`FlatList` /
 *   `RenderScene`), never to geometry.
 * - **Per-cell frames** — a cell's box within its column stays in the content
 *   path's `LayoutEngine`; if spanned-overlay geometry later needs it here, the
 *   designated additive extension point is a `cellFrames` map (add it beside
 *   `rows`; do not thread it through `RowGeometry`).
 * - **Per-row ledger versions** — deferred until the data track wires GEOMETRY
 *   bumps. The snapshot-level `epoch` is the geometry activation key today; a
 *   per-row version column is additive later.
 *
 * ## Build semantics
 *
 * `build` copies **scalar fields** out of each live `CellLayout` — it never
 * aliases the layout's `CellLayout` object, because `SheetLayoutSpec.make`
 * mutates `CellLayout.offsetPx` in place while rebasing footer rows
 * (`sheet-layout-spec.ts` footer rebase). Between frames it reuses the prior
 * snapshot's `RowGeometry` / `ColumnCell` objects **by reference** when every
 * field is equal (structural sharing, engine-spec §3), so `===` on a
 * `RowGeometry` is a truthful "this row's geometry did not move". The
 * `work` counters record the count-of-work (engine-spec §0 law 5 / REQ-OBS-3)
 * inside the frozen message, so fixtures assert amount-of-work from the
 * artifact itself.
 */

import type { Option } from "../core/option.ts";
import { ArtifactFreeze } from "@tento-core/artifact-freeze";

import type { CellLayout, CellLayoutOptions, SheetLayoutSpec } from "./layout-types.ts";
import type * as CoreCellLayouts from "@tento-core/sheet-layout/cell-layout";
import type * as CoreSheetLayouts from "@tento-core/sheet-layout/sheet-layout-spec";
import type { RowLocalId } from "../ids/row-local-id.ts";
import { RowNetworkKey } from "../ids/row-network-key.ts";
import type { SheetNetworkKey } from "../ids/sheet-network-key.ts";
import { elementAt } from "../core/checked-index.ts";
import type { RowSection } from "./scene.ts";

/**
 * The pre-paint geometry of one rendered surface (offsets, sizes, scroll window, apart from
 * content) that the paint and commit stages read.
 */
export interface GeometrySnapshot {
  readonly schemaVersion: 1;
  /** Immutable identity of the surface this geometry describes. */
  readonly identity: GeometrySnapshot.Identity;
  /** Caller-injected frame revision. Never read ambiently; excluded from `equals`. */
  readonly revision: GeometrySnapshot.Revision;
  /** Sheet-level vertical bands (header / body / footer heights + counts). */
  readonly vertical: GeometrySnapshot.VerticalBands;
  /** Per-row vertical geometry, in flat render order. */
  readonly rows: readonly GeometrySnapshot.RowGeometry[];
  /** Per-column horizontal geometry, keyed by `offset` (the join key). */
  readonly columns: readonly GeometrySnapshot.ColumnCell[];
  /** Standing-query window geometry (NOT live scroll — see module doc). */
  readonly window: GeometrySnapshot.ScrollWindow;
  /** Count-of-work for this build, carried inside the frozen message. */
  readonly work: GeometrySnapshot.BuildCounters;
}

/**
 * Types and values that go with `GeometrySnapshot`: `Identity`, `Revision`,
 * `VerticalBands`, `RowStacking`, `RowGeometry`, `ColumnCell`, and 7 more.
 */
export namespace GeometrySnapshot {
  export interface Identity {
    readonly sheetKey: SheetNetworkKey;
    readonly timescale: string;
  }

  export interface Revision {
    /** Seal mark the reader pinned (engine-spec §3 read-marks). */
    readonly tau: number;
    /** The geometry activation key for this frame. */
    readonly epoch: number;
  }

  export interface VerticalBands {
    readonly headerHeightPx: number;
    readonly bodyHeightPx: number;
    readonly footerHeightPx: number;
    readonly totalHeightPx: number;
    /** Layout-computed initial scroll seed (first body row's authored offset). */
    readonly initialScrollTopPx: number;
    readonly frozenRowCount: number;
    readonly frozenFlattenedRowCount: number;
    readonly footerRowCount: number;
    readonly footerFlattenedRowCount: number;
  }

  export interface RowStacking {
    readonly cellsPerColumn: number;
    readonly individualCellHeightPx: number;
    readonly visibleCellCount: number;
    readonly seeMoreHeightPx: number;
  }

  export interface RowGeometry {
    /** The row's wire key (`RowNetworkKey.of(rowLocalId)`). */
    readonly rowId: RowNetworkKey;
    /** Position in the flat render list (stable across skipped rows). */
    readonly rowIndex: number;
    readonly section: RowSection;
    readonly offsetPx: number;
    readonly cellHeightPx: number;
    readonly cellHeightWithChildrenPx: number;
    readonly stacking: Option<RowStacking>;
  }

  export interface ColumnCell {
    /** The join key. No date, no content identity. */
    readonly offset: number;
    readonly leftPx: number;
    readonly widthPx: number;
  }

  export interface ScrollWindow {
    readonly viewportWidthPx: number;
    readonly contentWidthPx: number;
    readonly firstColumnOffset: number;
    readonly lastColumnOffsetExclusive: number;
  }

  export interface BuildCounters {
    /** Rows whose layout entry was looked up (== `rows.length` of the input). */
    readonly rowLayoutsRead: number;
    /** Rows that had a layout entry and were emitted (read minus skipped). */
    readonly rowsEmitted: number;
    /** Emitted rows reused by reference from the previous snapshot. */
    readonly rowsReused: number;
    /** Columns reused by reference from the previous snapshot. */
    readonly columnsReused: number;
  }

  /**
   * The layout shape `build` reads. Extends the narrow
   * `./layout-types` `SheetLayoutSpec` (the pure `timescale` + row-layout
   * lookup) with the sheet-level vertical-band fields that tento-core's
   * `SheetLayoutSpec<Id>` class also carries. That class is NOT assignable to
   * this interface (its ambient `Option` admits `undefined`, its cell hints
   * `null`): a caller holding one — `handle.joinedTree(…).layout` — passes it
   * through `layoutInputOf`. We never value-import the core class (dep
   * direction + TDZ discipline).
   */
  export interface LayoutInput extends SheetLayoutSpec<RowLocalId> {
    readonly headerHeightPx: number;
    readonly bodyHeightPx: number;
    readonly footerRowsHeightPx: number;
    readonly totalHeightPx: number;
    readonly initialScrollTop: number;
    readonly numFrozenRowsLayout: { readonly numFrozen: number; readonly numFrozenFlattened: number };
    readonly numFooterRowsLayout: { readonly numFooter: number; readonly numFooterFlattened: number };
  }

  export interface RowInput {
    readonly id: RowLocalId;
    readonly section: RowSection;
    /** Full semantic position when the input contains only resident rows. */
    readonly rowIndex?: number;
  }

  export interface BuildInput {
    readonly sheetKey: SheetNetworkKey;
    readonly tau: number;
    readonly epoch: number;
    /** Live sheet layout (scalars copied out; never aliased). `timescale` reads `layout.timescale`. */
    readonly layout: LayoutInput;
    /** Flat render list. Rows without a `layout.getRowLayout` entry are skipped. */
    readonly rows: readonly RowInput[];
    readonly columns: readonly ColumnCell[];
    readonly window: ScrollWindow;
    /** Prior snapshot for reference reuse, or null on first build. */
    readonly previous: Option<GeometrySnapshot>;
  }

  /**
   * Build a `GeometrySnapshot` from a live layout + flat row/column lists.
   * Always builds (the caller decides the skip-cadence via `equals`); copies
   * scalars out of each `CellLayout`, reuses field-equal `RowGeometry` /
   * `ColumnCell` objects from `previous` by reference, and returns the
   * deep-frozen artifact.
   */
  export function build(input: BuildInput): GeometrySnapshot {
    const { layout } = input;

    const priorRowsById = new Map<string, RowGeometry>();
    const priorColsByOffset = new Map<number, ColumnCell>();
    if (input.previous) {
      for (const row of input.previous.rows) priorRowsById.set(row.rowId, row);
      for (const col of input.previous.columns) priorColsByOffset.set(col.offset, col);
    }

    let rowLayoutsRead = 0;
    let rowsReused = 0;
    const rows: RowGeometry[] = [];
    for (let i = 0; i < input.rows.length; i++) {
      const rowInput = elementAt(input.rows, i, "GeometrySnapshot: input.rows");
      rowLayoutsRead += 1;
      const cell = layout.getRowLayout(rowInput.id);
      // No layout entry — skip. The gap shows up as rowLayoutsRead > rowsEmitted.
      if (!cell) continue;

      const stackInfo = cell.stackingInfo;
      const stacking: Option<RowStacking> = stackInfo
        ? {
          cellsPerColumn: stackInfo.cellsPerColumn,
          individualCellHeightPx: stackInfo.individualCellHeight,
          visibleCellCount: stackInfo.visibleCellCount,
          seeMoreHeightPx: stackInfo.seeMoreHeight,
        }
        : null;

      // Fresh copy of scalar fields — never alias the live CellLayout
      // (footer rebase mutates its offsetPx in place).
      const fresh: RowGeometry = {
        rowId: RowNetworkKey.of(rowInput.id),
        rowIndex: rowInput.rowIndex ?? i,
        section: rowInput.section,
        offsetPx: cell.offsetPx,
        cellHeightPx: cell.cellHeightPx,
        cellHeightWithChildrenPx: cell.cellHeightWithChildrenPx,
        stacking,
      };

      const prior = priorRowsById.get(fresh.rowId);
      if (prior && rowGeometryEqual(prior, fresh)) {
        rows.push(prior);
        rowsReused += 1;
      } else {
        rows.push(fresh);
      }
    }

    let columnsReused = 0;
    const columns: ColumnCell[] = [];
    for (const columnInput of input.columns) {
      const fresh: ColumnCell = {
        offset: columnInput.offset,
        leftPx: columnInput.leftPx,
        widthPx: columnInput.widthPx,
      };
      const prior = priorColsByOffset.get(fresh.offset);
      if (prior && columnCellEqual(prior, fresh)) {
        columns.push(prior);
        columnsReused += 1;
      } else {
        columns.push(fresh);
      }
    }

    const snapshot: GeometrySnapshot = {
      schemaVersion: 1,
      identity: {
        sheetKey: input.sheetKey,
        timescale: layout.timescale,
      },
      revision: {
        tau: input.tau,
        epoch: input.epoch,
      },
      vertical: {
        headerHeightPx: layout.headerHeightPx,
        bodyHeightPx: layout.bodyHeightPx,
        footerHeightPx: layout.footerRowsHeightPx,
        totalHeightPx: layout.totalHeightPx,
        initialScrollTopPx: layout.initialScrollTop,
        frozenRowCount: layout.numFrozenRowsLayout.numFrozen,
        frozenFlattenedRowCount: layout.numFrozenRowsLayout.numFrozenFlattened,
        footerRowCount: layout.numFooterRowsLayout.numFooter,
        footerFlattenedRowCount: layout.numFooterRowsLayout.numFooterFlattened,
      },
      rows,
      columns,
      window: {
        viewportWidthPx: input.window.viewportWidthPx,
        contentWidthPx: input.window.contentWidthPx,
        firstColumnOffset: input.window.firstColumnOffset,
        lastColumnOffsetExclusive: input.window.lastColumnOffsetExclusive,
      },
      work: {
        rowLayoutsRead,
        rowsEmitted: rows.length,
        rowsReused,
        columnsReused,
      },
    };

    return ArtifactFreeze.deep(snapshot);
  }

  /**
   * The SDK's one reading of a tento-core layout is `cellLayoutOfCore` (one
   * row); `layoutSpecOf` maps it over the rows, `layoutInputOf` adds the
   * scalars, and `cellLayoutOf` answers one row. The DOM binder reads
   * `layoutSpecOf` (since 3894ae3a2e), so no consumer keeps a converter of
   * its own.
   *
   * `core` (tento-core's `SheetLayoutSpec`, what `handle.joinedTree(…).layout`
   * computes) as the render tier's `LayoutInput`: the same scalars and
   * timescale, and every row's `CellLayout` read into the SDK's shape — an
   * absent hint set or stacking is `null`, a `null` / `undefined` hint is left
   * out. Read once, when called: a core layout is immutable once `make`
   * returns, so a caller rendering the same layout again keeps the answer.
   */
  export function layoutInputOf(core: CoreSheetLayouts.SheetLayoutSpec<RowLocalId>): LayoutInput {
    return {
      ...layoutSpecOf(core),
      headerHeightPx: core.headerHeightPx,
      bodyHeightPx: core.bodyHeightPx,
      footerRowsHeightPx: core.footerRowsHeightPx,
      totalHeightPx: core.totalHeightPx,
      initialScrollTop: core.initialScrollTop,
      numFrozenRowsLayout: {
        numFrozen: core.numFrozenRowsLayout.numFrozen,
        numFrozenFlattened: core.numFrozenRowsLayout.numFrozenFlattened,
      },
      numFooterRowsLayout: {
        numFooter: core.numFooterRowsLayout.numFooter,
        numFooterFlattened: core.numFooterRowsLayout.numFooterFlattened,
      },
    };
  }

  /**
   * The rows part of a core layout (`timescale` and every row's `CellLayout`)
   * as the SDK's `SheetLayoutSpec`, read as `layoutInputOf` reads it (that
   * function is this plus the scalars). For a reader that needs only the rows
   * (a binder's render plans): it reads nothing else off `core`, so a layout
   * that carries only `timescale` and `rowLayout` (a test's fake) serves.
   */
  export function layoutSpecOf(
    core: Pick<CoreSheetLayouts.SheetLayoutSpec<RowLocalId>, "timescale" | "rowLayout">,
  ): SheetLayoutSpec<RowLocalId> {
    const rowLayout = new Map<RowLocalId, CellLayout>();
    for (const [id, layout] of core.rowLayout) rowLayout.set(id, cellLayoutOfCore(layout));
    return {
      timescale: core.timescale,
      rowLayout,
      getRowLayout: (id) => rowLayout.get(id) ?? null,
    };
  }

  /**
   * One core row layout (tento-core's `CellLayout`) as the SDK's `CellLayout`,
   * read as `layoutInputOf` reads each row: an absent hint set or stacking is
   * `null`, a `null` / `undefined` hint is left out. The one converter: a
   * renderer holding a single core cell layout reads it through here.
   */
  export function cellLayoutOf(core: CoreCellLayouts.CellLayout): CellLayout {
    return cellLayoutOfCore(core);
  }

  /**
   * Cheap field-wise equality used by the caller's skip-cadence. Compares
   * `identity` / `vertical` / `rows` / `columns` / `window`; the frame
   * `revision` is EXCLUDED (two snapshots of identical geometry at different
   * `tau`/`epoch` are equal for skip purposes).
   */
  export function equals(a: GeometrySnapshot, b: GeometrySnapshot): boolean {
    if (a.identity.sheetKey !== b.identity.sheetKey) return false;
    if (a.identity.timescale !== b.identity.timescale) return false;
    if (!verticalEqual(a.vertical, b.vertical)) return false;

    if (a.rows.length !== b.rows.length) return false;
    for (const [i, row] of a.rows.entries()) {
      if (!rowGeometryEqual(row, elementAt(b.rows, i, "GeometrySnapshot.equals: b.rows"))) return false;
    }

    if (a.columns.length !== b.columns.length) return false;
    for (const [i, column] of a.columns.entries()) {
      if (!columnCellEqual(column, elementAt(b.columns, i, "GeometrySnapshot.equals: b.columns"))) {
        return false;
      }
    }

    return windowEqual(a.window, b.window);
  }

  function verticalEqual(a: VerticalBands, b: VerticalBands): boolean {
    return (
      a.headerHeightPx === b.headerHeightPx &&
      a.bodyHeightPx === b.bodyHeightPx &&
      a.footerHeightPx === b.footerHeightPx &&
      a.totalHeightPx === b.totalHeightPx &&
      a.initialScrollTopPx === b.initialScrollTopPx &&
      a.frozenRowCount === b.frozenRowCount &&
      a.frozenFlattenedRowCount === b.frozenFlattenedRowCount &&
      a.footerRowCount === b.footerRowCount &&
      a.footerFlattenedRowCount === b.footerFlattenedRowCount
    );
  }

  function rowGeometryEqual(a: RowGeometry, b: RowGeometry): boolean {
    if (a.rowId !== b.rowId) return false;
    if (a.rowIndex !== b.rowIndex) return false;
    if (a.section !== b.section) return false;
    if (a.offsetPx !== b.offsetPx) return false;
    if (a.cellHeightPx !== b.cellHeightPx) return false;
    if (a.cellHeightWithChildrenPx !== b.cellHeightWithChildrenPx) return false;
    return rowStackingEqual(a.stacking, b.stacking);
  }

  function rowStackingEqual(a: Option<RowStacking>, b: Option<RowStacking>): boolean {
    if (!a || !b) return !a && !b;
    return (
      a.cellsPerColumn === b.cellsPerColumn &&
      a.individualCellHeightPx === b.individualCellHeightPx &&
      a.visibleCellCount === b.visibleCellCount &&
      a.seeMoreHeightPx === b.seeMoreHeightPx
    );
  }

  function columnCellEqual(a: ColumnCell, b: ColumnCell): boolean {
    return a.offset === b.offset && a.leftPx === b.leftPx && a.widthPx === b.widthPx;
  }

  function windowEqual(a: ScrollWindow, b: ScrollWindow): boolean {
    return (
      a.viewportWidthPx === b.viewportWidthPx &&
      a.contentWidthPx === b.contentWidthPx &&
      a.firstColumnOffset === b.firstColumnOffset &&
      a.lastColumnOffsetExclusive === b.lastColumnOffsetExclusive
    );
  }
}

/** `core` as the SDK's `CellLayout`: an absent hint set or stacking is `null`. */
function cellLayoutOfCore(core: CoreCellLayouts.CellLayout): CellLayout {
  const stacking = core.stackingInfo;
  return {
    offsetPx: core.offsetPx,
    cellHeightPx: core.cellHeightPx,
    cellHeightWithChildrenPx: core.cellHeightWithChildrenPx,
    options: core.options != null ? cellLayoutOptionsOfCore(core.options) : null,
    stackingInfo: stacking != null
      ? {
        cellsPerColumn: stacking.cellsPerColumn,
        individualCellHeight: stacking.individualCellHeight,
        visibleCellCount: stacking.visibleCellCount,
        seeMoreHeight: stacking.seeMoreHeight,
      }
      : null,
  };
}

/** `core` as the SDK's `CellLayoutOptions`: a `null` or `undefined` hint is left out. */
function cellLayoutOptionsOfCore(core: CoreCellLayouts.CellLayoutOptions): CellLayoutOptions {
  return {
    ...presentHint("background", core.background),
    ...presentHint("padding", core.padding),
    ...presentHint("marginInline", core.marginInline),
    ...presentHint("marginBlock", core.marginBlock),
    ...presentHint("drawVerticalDivider", core.drawVerticalDivider),
    ...presentHint("card", core.card),
    ...presentHint("canDrawCardBorder", core.canDrawCardBorder),
    ...presentHint("labelHeightPx", core.labelHeightPx),
    ...presentHint("disableContainment", core.disableContainment),
  };
}

/** `{ [key]: value }` when `value` is set, `{}` otherwise. */
function presentHint<K extends keyof CellLayoutOptions, V>(key: K, value: Option<V> | undefined): Partial<Record<K, V>> {
  const out: Partial<Record<K, V>> = {};
  if (value != null) out[key] = value;
  return out;
}
